// src/views/Reports.jsx
import React, { useEffect, useMemo, useState, Suspense } from 'react';
import './reports.css';
import { FaExpand } from 'react-icons/fa';
import ReportExpandModal from './ReportExpandModal.jsx';

const Finance       = React.lazy(() => import('./ReportFinance.jsx'));
const Receiving     = React.lazy(() => import('./ReportReceiving.jsx'));
const Orders        = React.lazy(() => import('./ReportOrders.jsx'));
const ReportEOrders = React.lazy(() => import('./ReportEOrders.jsx'));
const Inventory     = React.lazy(() => import('./ReportInventory.jsx'));

const API = import.meta.env.VITE_API_URL;

// ---- Date preset helpers (match Receipts) ----
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (y) => new Date(y, 0, 1);
const endOfYear = (y) => new Date(y, 11, 31);

/* ---------- role helpers ---------- */
function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u)  { return (u.clientid ?? u.clientId ?? u.client_id) ?? ''; }
function getUserClientName(u){ return u.clientname ?? u.clientName ?? ''; }
/* ---------------------------------- */

/**
 * Lightweight client-only Finance card:
 * Open invoices for the logged-in client (summary line only)
 */
function ClientFinanceOpenInvoices({ from, to, clientId, clientName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to)   p.set('to', to);
    if (clientId) p.set('clientid', clientId);
    return p.toString();
  }, [from, to, clientId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/reports/invoices/open-by-client?${params}`);
        const data = await res.json();
        if (!mounted) return;
        // API returns an array [{clientId, clientName, openCount, amountDue}, ...]
        const mine = Array.isArray(data) ? data.filter(r => String(r.clientId) === String(clientId)) : [];
        setRows(mine);
      } catch {
        if (mounted) setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [params, clientId]);

  const money = (n) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(Number(n || 0));

  return (
    <div className="report-card">
      <div className="report-card-hdr">
        <h2>Open Invoices — {clientName || `Client #${clientId}`}</h2>
        <div className="report-actions">
          <button className="expand-btn" title="Expand" onClick={() => setExpanded(true)}><FaExpand size={16} /></button>
        </div>
      </div>
      {loading ? (
        <div className="loading">Loading…</div>
      ) : rows.length ? (
        <div className="table-wrap">
          <table className="report-table">
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ textAlign:'right' }}>Open Count</th>
                <th style={{ textAlign:'right' }}>Amount Due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.clientId}>
                  <td>{r.clientName || `Client #${r.clientId}`}</td>
                  <td style={{ textAlign:'right' }}>{Number(r.openCount || 0).toLocaleString()}</td>
                  <td style={{ textAlign:'right' }}>{money(r.amountDue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="loading">No open invoices.</div>
      )}
      <div style={{ marginTop: 8, fontSize: 12, opacity: .8 }}>
        Date range: {from || '—'} to {to || '—'}
      </div>

      <ReportExpandModal open={expanded} title={`Open Invoices — ${clientName || `Client #${clientId}`}`} onClose={() => setExpanded(false)}>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : rows.length ? (
          <div className="table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th style={{ textAlign:'right' }}>Open Count</th>
                  <th style={{ textAlign:'right' }}>Amount Due</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.clientId}>
                    <td>{r.clientName || `Client #${r.clientId}`}</td>
                    <td style={{ textAlign:'right' }}>{Number(r.openCount || 0).toLocaleString()}</td>
                    <td style={{ textAlign:'right' }}>{money(r.amountDue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="loading">No open invoices.</div>
        )}
        <div style={{ marginTop: 8, fontSize: 12, opacity: .8 }}>
          Date range: {from || '—'} to {to || '—'}
        </div>
      </ReportExpandModal>
    </div>
  );
}

export default function Reports() {
  const user = getCurrentUser();
  const IS_CLIENT = isClientUser(user);

  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('reports_tab') || 'finance');
  useEffect(() => localStorage.setItem('reports_tab', activeTab), [activeTab]);

  // Shared filters
  const [datePreset, setDatePreset] = useState(() => localStorage.getItem('reports_date_preset') || '');
  const [from, setFrom] = useState(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const qFrom = qs.get('from');
      if (qFrom !== null) return qFrom; // respect explicit empty string too
      const saved = localStorage.getItem('reports_from');
      if (saved !== null) return saved; // use saved even if empty (cleared)
    } catch {}
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth() - 5, 1);
    return start.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const qTo = qs.get('to');
      if (qTo !== null) return qTo; // respect explicit empty string too
      const saved = localStorage.getItem('reports_to');
      if (saved !== null) return saved; // use saved even if empty (cleared)
    } catch {}
    return new Date().toISOString().slice(0, 10);
  });

  // Apply saved date preset on mount if no explicit query overrides
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const hasQsFrom = qs.has('from');
      const hasQsTo = qs.has('to');
      if (!hasQsFrom && !hasQsTo && datePreset) {
        applyDatePreset(datePreset, { triggerPersist: false });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client dropdown (restricted for client users to allowed list from API)
  const [clientId, setClientId] = useState(() => localStorage.getItem('reports_client') || 'all');

  const [ordersDateField, setOrdersDateField] = useState(
    () => localStorage.getItem('reports_orders_datefield') || 'orderdate'
  );

  useEffect(() => localStorage.setItem('reports_from', from), [from]);
  useEffect(() => localStorage.setItem('reports_to', to), [to]);
  useEffect(() => { try { localStorage.setItem('reports_date_preset', datePreset || ''); } catch {} }, [datePreset]);
  // Keep URL query in sync for persistence on refresh/share
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (from) url.searchParams.set('from', from); else url.searchParams.delete('from');
      if (to)   url.searchParams.set('to', to);     else url.searchParams.delete('to');
      window.history.replaceState({}, '', url);
    } catch {}
  }, [from, to]);
  useEffect(() => { localStorage.setItem('reports_client', clientId); }, [clientId]);
  useEffect(() => localStorage.setItem('reports_orders_datefield', ordersDateField), [ordersDateField]);

  // Clients dropdown
  const [clients, setClients] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/reports/filters/clients`);
        const data = res.ok ? await res.json() : [];
        const clean = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name })).filter(c => c.id && c.name);
        setClients(clean);
        if (IS_CLIENT) {
          // Allow 'all' for client users; normalize only if selection is neither 'all' nor a valid id
          if (clientId !== 'all' && !clean.find(c => String(c.id) === String(clientId))) {
            setClientId(clean[0]?.id || '');
          }
        }
      } catch { setClients([]); }
    })();
  }, []);

  // If selection becomes invalid (after reload), normalize to first allowed/internal 'all'
  useEffect(() => {
    if (!clients.length) return;
    const ok = clients.find(c => String(c.id) === String(clientId)) || clientId === 'all';
    if (!ok) setClientId('all');
  }, [clients]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Reports', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  // Params shared to build links
  const baseParams = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (clientId && clientId !== 'all') p.set('clientid', clientId);
    return p;
  }, [from, to, clientId]);

  // “Run Reports” forces children to refetch
  const [refreshKey, setRefreshKey] = useState(0);
  const onFiltersKeyDown = (e) => { if (e.key === 'Enter') setRefreshKey(k => k + 1); };

  const selectedClientLabel = (() => {
    if (!clientId || clientId === 'all') return 'All Clients';
    const hit = clients.find(c => String(c.id) === String(clientId));
    return hit?.name || `Client #${clientId}`;
  })();

  // Date preset handler (updates from/to)
  const applyDatePreset = (preset, opts = {}) => {
    const today = new Date();
    let f = '', t = '';
    switch (preset) {
      case 'all_time': { f = ''; t = ''; break; }
      case 'today': { const d = fmtYMD(today); f = d; t = d; break; }
      case 'yesterday': { const y = new Date(today); y.setDate(y.getDate() - 1); const d = fmtYMD(y); f = d; t = d; break; }
      case 'this_week': { const s = startOfWeekMon(today); f = fmtYMD(s); t = fmtYMD(today); break; }
      case 'last_week': { const s0 = startOfWeekMon(today); const s = new Date(s0); s.setDate(s0.getDate() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'this_month': { const s = startOfMonth(today); const e = endOfMonth(today); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'last_month': { const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)); const e = endOfMonth(s); f = fmtYMD(s); t = fmtYMD(e); break; }
      case 'this_year': { const s = startOfYear(today.getFullYear()); f = fmtYMD(s); t = fmtYMD(today); break; }
      case 'last_year': { const y = today.getFullYear() - 1; f = fmtYMD(startOfYear(y)); t = fmtYMD(endOfYear(y)); break; }
      default: {
        setDatePreset('');
        if (opts.triggerPersist !== false) try { localStorage.setItem('reports_date_preset', ''); } catch {}
        return;
      }
    }
    setFrom(f); setTo(t); setDatePreset(preset);
    if (opts.triggerPersist !== false) try { localStorage.setItem('reports_date_preset', preset); } catch {}
  };

  return (
    <div className="reports-wrapper">
      <div className="view-tabs" role="tablist" aria-label="Report Types">
        {['finance','receiving','orders','eorders','inventory'].map(t => (
          <button
            key={t}
            role="tab"
            aria-selected={activeTab === t}
            className={`tab ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="reports-filters" onKeyDown={onFiltersKeyDown}>
        {activeTab === 'orders' && (
          <label>
            Date:
            <select value={ordersDateField} onChange={e => setOrdersDateField(e.target.value)}>
              <option value="orderdate">Ordered</option>
              <option value="shippeddate">Shipped</option>
            </select>
          </label>
        )}
        <label title="Quick date ranges">
          Preset:
          <select value={datePreset} onChange={e => applyDatePreset(e.target.value)}>
            <option value="">Custom…</option>
            <option value="all_time">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
          </select>
        </label>
        <label>From <input
          type="date"
          value={from}
          onChange={e => { const v = e.target.value; setFrom(v); setDatePreset(''); try { localStorage.setItem('reports_from', v); localStorage.setItem('reports_date_preset', ''); } catch {} }}
        /></label>
        <label>To <input
          type="date"
          value={to}
          onChange={e => { const v = e.target.value; setTo(v); setDatePreset(''); try { localStorage.setItem('reports_to', v); localStorage.setItem('reports_date_preset', ''); } catch {} }}
        /></label>

        <label>
          Client
          <select
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            title={IS_CLIENT ? 'Select one of your companies' : 'Choose client'}
          >
            {IS_CLIENT ? (
              <option value="all">--ALL--</option>
            ) : (
              <option value="all">All Clients</option>
            )}
            {clients.map(c => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <button className="btn" onClick={() => setRefreshKey(k => k + 1)}>Run Reports</button>
      </div>

      <div className="reports-grid" role="tabpanel" aria-live="polite">
        <Suspense fallback={<div className="loading">Loading…</div>}>
          {activeTab === 'finance' && (
            IS_CLIENT ? (
              <ClientFinanceOpenInvoices
                key={`finance-client-${refreshKey}`}
                from={from}
                to={to}
                clientId={String(clientId)}
                clientName={selectedClientLabel}
              />
            ) : (
              <Finance key={`finance-${refreshKey}`} baseParams={baseParams} from={from} to={to} clientId={clientId} />
            )
          )}

          {activeTab === 'receiving' && (
            <Receiving key={`receiving-${refreshKey}`} baseParams={baseParams} from={from} to={to} clientId={clientId} />
          )}

          {activeTab === 'orders' && (
            <Orders key={`orders-${refreshKey}`} baseParams={baseParams} from={from} to={to} clientId={clientId} dateField={ordersDateField} />
          )}

          {activeTab === 'eorders' && (
            <ReportEOrders key={`eorders-${refreshKey}`} baseParams={baseParams} />
          )}

          {activeTab === 'inventory' && (
            <Inventory key={`inventory-${refreshKey}`} baseParams={baseParams} from={from} to={to} clientId={clientId} />
          )}
        </Suspense>
      </div>
    </div>
  );
}
